import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Check, X, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { authHeaders } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("pending");
  const [message, setMessage] = useState("Verifying your payment...");
  const attempts = useRef(0);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setStatus("error"); setMessage("Missing session id"); return; }

    const poll = async () => {
      if (attempts.current >= 8) {
        setStatus("error");
        setMessage("Payment status timeout. Please refresh your dashboard in a moment.");
        return;
      }
      try {
        const { data } = await axios.get(`${API}/payments/status/${sid}`, { headers: authHeaders() });
        if (data.payment_status === "paid") {
          setStatus("success"); setMessage("Payment successful! Your subscription is active.");
          return;
        }
        if (data.status === "expired") {
          setStatus("error"); setMessage("Payment session expired.");
          return;
        }
        attempts.current += 1;
        setTimeout(poll, 2000);
      } catch {
        attempts.current += 1;
        setTimeout(poll, 2000);
      }
    };
    poll();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="px-6 sm:px-12 lg:px-24 py-24 max-w-md mx-auto text-center">
      <div className="bg-slate-900 border border-slate-800 p-10 rounded-sm">
        {status === "pending" && (
          <>
            <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-6" />
            <h2 className="font-display font-bold text-2xl text-slate-100 mb-2">Processing</h2>
            <p className="text-slate-400 text-sm" data-testid="payment-status-message">{message}</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-16 h-16 bg-emerald-600/20 border border-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-emerald-500" />
            </div>
            <h2 className="font-display font-black uppercase tracking-tighter text-2xl text-slate-50 mb-2">Payment Successful</h2>
            <p className="text-slate-400 text-sm mb-8" data-testid="payment-status-message">{message}</p>
            <Link to="/dashboard" data-testid="payment-go-dashboard" className="block py-3 bg-orange-600 text-white font-bold uppercase tracking-wider hover:bg-orange-500 rounded-sm">Go to Dashboard</Link>
          </>
        )}
        {status === "error" && (
          <>
            <div className="w-16 h-16 bg-red-600/20 border border-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <X className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="font-display font-bold text-2xl text-slate-100 mb-2">Issue</h2>
            <p className="text-slate-400 text-sm mb-8" data-testid="payment-status-message">{message}</p>
            <button onClick={() => navigate("/pricing")} className="py-3 px-6 border border-slate-700 text-slate-200 font-bold uppercase tracking-wider hover:border-orange-500 rounded-sm">Back to Pricing</button>
          </>
        )}
      </div>
    </div>
  );
}
